import { forwardRef, useContext, useEffect, useRef, useState } from "react"
import { Draggable, Droppable } from "react-beautiful-dnd"

import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table"
import CancelOrderIcon from "../../../assets/icons/CancelOrderIcon"
import MoveEllipsisIcon from "../../../assets/icons/MoveEllipsisIcon"
import SortArrowIcon from "../../../assets/icons/SortArrowIcon"
import {
  cDeadLineSlug,
  cInvolvedsSlug,
  cResponsiblesSlug,
  cStatusSlug,
  dConclusionDateSlug,
  dCreationDateSlug,
} from "../../../constans/activity-slugs"
import AuthContext from "../../../contexts/Auth"
import { BlocksContext } from "../../../contexts/Blocks"
import DragAndDropContext from "../../../contexts/DragAndDropContext"
import { fnFormatDate, fnFormatDateFromUTC } from "../../../utils/date"
import RcmpLoading from "../../atoms/Loading"
import Table from "../../atoms/Table"
import RcmpTableField from "../../organisms/TableField"
import styles from "./index.module.scss"
import { calculate, getVariablesOnFormula } from "../CalcField/logic"
import FieldsContext from "../../../contexts/Fields"

export default function RcmpActivitiesTable(props, ref) {
  const {
    aData,
    fnHandleModalEditActivity,
    fnReorderActivitiesSubmit,
    fnSubmitGetActivities,
    oBlock,
    bCanDrop,
    fnMoveActivitySubmit,
    fnUpdateActivityBlocks,
    aBlocksToList,
    aAllFields,
    bCanChange,
    oCurrentModel,
    fnSetABlocksToList,
    oCurrentBoard,
  } = props
  const [aNData, fnSetANData] = useState(aData)
  const [aColumns, fnSetColumns] = useState([])
  const [aColumnOrder, fnSetColumnOrder] = useState([])
  const [bFieldDisabledEdit, fnSetBFieldDisabledEdit] = useState(true)
  const [aSorting, fnSetASorting] = useState([])
  const [aTableRowUnsorted, fnSetATableRowUnsorted] = useState([-1])

  const {
    aBlocks,
    fnSetABlocksASortedOrder,
    aBlocksASortedOrder,
    fnFetchActivitiesByBlock,
    oActivityByBlockRes,
    oActivitiesRequestDone,
  } = useContext(BlocksContext)
  const { oUser } = useContext(AuthContext)
  const cUserTimezone = oUser?.data?.user_ctimezone || "America/Sao_Paulo"
  const tableScale = useRef(null)

  const { oDragResult, oOnDragUpdate } = useContext(DragAndDropContext)
  const { oAllFieldsRes } = useContext(FieldsContext)

  function fnToggleSorting(nColumnId) {
    if (!aSorting.length || aSorting[0]?.id !== nColumnId) {
      fnSetASorting([{ id: nColumnId, desc: false }])
      return [{ id: nColumnId, desc: false }]
    }

    fnSetASorting((e) => [{ id: nColumnId, desc: !e[0].desc }])
    return [{ id: nColumnId, desc: !aSorting[0].desc }]
  }
  //usar para validar o que deve ser salvo no DND
  const table = useReactTable({
    data: aNData || [],
    columns: aColumns,
    columnResizeMode: "onChange",
    getCoreRowModel: getCoreRowModel(),
    state: {
      columnOrder: aColumnOrder,
      sorting: aSorting,
    },
    onColumnOrderChange: fnSetColumnOrder,
    // onSortingChange: fnToggleSorting,
    // getSortedRowModel: getSortedRowModel(),
  })

  useEffect(() => {
    if (!Object?.keys(table?.getState()?.columnSizing)?.length) return

    const aColumnsSize = Object.keys(table.getState().columnSizing)?.map((key) => ({
      columnId: key,
      column_size: table.getState().columnSizing[key],
      block_id: oBlock.opbl_nid,
    }))
    fnSaveWidthColumnsStorage(aColumnsSize)
  }, [table?.getState()?.columnSizing])

  function fnSaveWidthColumnsStorage(aColumns) {
    const aColumnsStorage = JSON.parse(localStorage.getItem("column_width") || "[]")
    if (!aColumnsStorage?.length) {
      localStorage.setItem("column_width", JSON.stringify(aColumns))
      return
    }

    const aNewColumnsStorage = aColumnsStorage
    for (const oColumn of aColumns) {
      const oExistingColumn = aColumnsStorage.find(
        (oColumnStorage) =>
          Number(oColumnStorage.columnId) === Number(oColumn.columnId) &&
          oColumnStorage.block_id === oColumn.block_id,
      )
      if (oExistingColumn) {
        oExistingColumn.column_size = oColumn?.column_size
      } else {
        aNewColumnsStorage.push(oColumn)
      }
    }

    const aUnicColumnsStorage = aNewColumnsStorage.filter(
      (obj, index) =>
        index ===
        aNewColumnsStorage.findIndex(
          (o) =>
            Number(obj.columnId) === Number(o.columnId) && obj.block_id === o.block_id,
        ),
    )

    localStorage.setItem("column_width", JSON.stringify(aUnicColumnsStorage))
  }

  function fnStopSorting(e) {
    e.stopPropagation()
    if (oActivityByBlockRes.loading) return
    const columnOrder = JSON.parse(localStorage.getItem("columnOrder") || "[]")
    const index = columnOrder.findIndex(
      (item) => item.blockId === oBlock.opbl_nid && item.order.length > 0,
    )
    if (index !== -1) {
      columnOrder.splice(index, 1)
      localStorage.setItem("columnOrder", JSON.stringify(columnOrder))
    }
    fnSetASorting([])
    fnFetchActivitiesByBlock(oBlock.opbl_nid)
  }

  function SortArrowAnimation() {
    return (
      <div className={styles.arrowIcon}>
        <SortArrowIcon
          className={
            aSorting.length && !aSorting[0]?.desc ? styles.ascending : styles.descending
          }
        />
        <CancelOrderIcon onClick={fnStopSorting} />
      </div>
    )
  }

  function fnSortActivities(columnId) {
    const response = fnToggleSorting(columnId)
    // response && fnFetchSortedActivitiesByBlock(oBlock.opbl_nid, columnId, response)
    fnFetchActivitiesByBlock(oBlock.opbl_nid)
  }

  const FnPortalAwereItem = forwardRef(({ item, index, flexRender }, ref) => {
    const nColumnId = item?.id || -1
    return (
      <Table.HeadData
        ref={ref}
        key={index}
        id={`tHeadCollum${index}`}
        style={getItemStyle(item)}
        colSpan={item.colSpan}
        index={index}
        onClick={() => fnSortActivities(item.column.id)}
      >
        <p>
          {item.isPlaceholder
            ? null
            : flexRender(item.column.columnDef.header, item.getContext())}
          {/* Code to sort columns */}
          {nColumnId === aSorting[0]?.id && <SortArrowAnimation />}
        </p>

        <div
          className={styles.resize}
          onMouseDown={item.getResizeHandler()}
          onTouchStart={item.getResizeHandler()}
        >
          <div />
        </div>
      </Table.HeadData>
    )
  })

  const aBlockHiddenFields = oBlock?.table_hidden_fields || []
  const aVisibleFields =
    aBlockHiddenFields?.length > 0
      ? oCurrentModel?.fields
          ?.filter(
            (f) => !aBlockHiddenFields.find((h) => h?.ophf_ombf_nid === f?.ombf_nid),
          )
          ?.map((f) => f?.field) || []
      : oCurrentModel?.fields?.map((f) => f?.field) || []

  function getValue(aValues, data) {
    let cValue = ""
    cValue = aValues?.values?.find((value) => value?.opfv_opfd_nid == data?.opfd_nid)
    const cValueString = cValue?.opfv_cvalue?.toString()
    const oValue = {
      value_to_render: cValue?.opfv_cvalue,
      opfv_cformula: cValue?.opfv_cformula,
      opfv_cvalue: cValueString
        ?.normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase(),
      opfv_opfd_nid: cValue?.opfv_opfd_nid,
    }

    if (data.opfd_ctype === "SELECTION") {
      let value = aValues?.values?.find((value) => value?.opfv_opfd_nid == data?.opfd_nid)

      if (data?.opfd_cslug === cStatusSlug) {
        const statusText =
          value?.selected_option?.opfs_coption_name === "Done" ? "Concluida" : "Pendente"
        return {
          opfv_cvalue: statusText
            ?.normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .toLowerCase(),
          value_to_render_select: statusText,
          opfv_opfd_nid: data.opfd_nid,
          oOptionColor:
            value?.selected_option?.opfs_coption_name === "Done" ? "#1ecb4f" : "#415364",
        }
      } else {
        return {
          value_to_render_select: value?.selected_option?.opfs_coption_name,
          opfv_cvalue: value?.selected_option?.opfs_coption_name
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .toLowerCase(),
          opfv_opfd_nid: data.opfd_nid,
          oOptionColor: value?.selected_option?.opfs_coption_color,
        }
      }
    }

    if (data?.opfd_cslug === cDeadLineSlug) {
      let dDAteToConvert = oValue?.opfv_cvalue
      if (dDAteToConvert?.includes("t")) {
        dDAteToConvert = fnFormatDate(
          oValue?.opfv_cvalue,
          "YYYY-MM-DD HH:mm",
          false,
          cUserTimezone,
          "YYYY-MM-DD[T]HH:mm:ss.SSS",
        )
      }

      const formattedDate = fnFormatDateFromUTC(
        dDAteToConvert,
        "YYYY/MM/DD HH:mm",
        cUserTimezone,
      )
      return {
        opfv_cvalue: dDAteToConvert,
        opfv_opfd_nid: data.opfd_nid,
      }
    }
    if (data?.opfd_cslug === dCreationDateSlug) {
      let dDAteToConvert = oValue?.opfv_cvalue
      if (dDAteToConvert?.includes("t")) {
        dDAteToConvert = fnFormatDate(
          oValue?.opfv_cvalue,
          "YYYY-MM-DD HH:mm",
          false,
          cUserTimezone,
          "YYYY-MM-DD[T]HH:mm:ss.SSS",
        )
      }
      const formattedDate = fnFormatDateFromUTC(
        dDAteToConvert,
        "YYYY/MM/DD HH:mm",
        cUserTimezone,
      )
      return {
        opfv_cvalue: formattedDate,
        opfv_opfd_nid: data.opfd_nid,
      }
    }
    if (data?.opfd_cslug === dConclusionDateSlug) {
      let dDAteToConvert = oValue?.opfv_cvalue
      if (dDAteToConvert?.includes("t")) {
        dDAteToConvert = fnFormatDate(
          oValue?.opfv_cvalue,
          "YYYY-MM-DD HH:mm",
          false,
          cUserTimezone,
          "YYYY-MM-DD[T]HH:mm:ss.SSS",
        )
      }
      const formattedDate = fnFormatDateFromUTC(
        dDAteToConvert,
        "YYYY/MM/DD HH:mm",
        cUserTimezone,
      )
      return {
        opfv_cvalue: dDAteToConvert,
        opfv_opfd_nid: data.opfd_nid,
      }
    }

    if (data.opfd_cslug === cResponsiblesSlug) {
      let aResponsibles = aValues?.responsibles?.map((activity) =>
        activity?.user_cname
          ?.normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .toLowerCase(),
      )
      let aResponsiblesToRender = aValues?.responsibles?.map((activity) => ({
        user_cname: activity?.user_cname,
        user_cavatar: activity?.user_cavatar,
      }))
      return {
        opfv_cvalue: aResponsibles[0],
        opfv_opfd_nid: data?.opfd_nid,
        value_to_render: aResponsiblesToRender,
      }
    }

    if (data.opfd_cslug === cInvolvedsSlug) {
      let aInvolveds = aValues?.involveds?.map((responsible) => {
        return responsible?.user_cname
          ?.normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .toLowerCase()
      })
      let aInvolvedsToRender = aValues?.involveds?.map((responsible) => {
        return {
          user_cname: responsible?.user_cname,
          user_cavatar: responsible?.user_cavatar,
        }
      })
      return {
        opfv_cvalue: aInvolveds[0],
        value_to_render: aInvolvedsToRender,
        opfv_opfd_nid: data?.opfd_nid,
      }
    }

    if (data.opfd_ctype?.toLowerCase() === "date") {
      const formattedDate = fnFormatDateFromUTC(
        new Date(oValue?.opfv_cvalue),
        "YYYY/MM/DD HH:mm",
        cUserTimezone,
      )

      return {
        opfv_cvalue: oValue?.opfv_cvalue,
        opfv_opfd_nid: data.opfd_nid,
      }
    }

    if (
      data.opfd_ctype?.toLowerCase() === "text" ||
      data.opfd_ctype?.toLowerCase() === "description"
    ) {
      return {
        value_to_render: oValue?.value_to_render,
        opfv_cvalue: oValue?.opfv_cvalue,
        opfv_opfd_nid: data.opfd_nid,
      }
    }

    if (data.opfd_ctype?.toLowerCase() === "peoples") {
      //add 'z' to the end of the string to avoid empty values, so it will be the last one in the list

      return {
        opfv_cvalue: cValue?.opfv_cvalue
          ? cValue?.opfv_cvalue[0]?.user_cname
              ?.normalize("NFD")
              .replace(/[\u0300-\u036f]/g, "")
              .toLowerCase() || "z"
          : "z",
        value_to_render: oValue.value_to_render || [],
        opfv_opfd_nid: data.opfd_nid,
      }
    }

    const calculateValue = (formula, nPrecision) => {
      const formulaIds = getVariablesOnFormula(formula)
      formulaIds.forEach((id) => {
        const value = aValues?.values?.find((value) => value?.opfv_opfd_nid == id) || {
          opfv_cvalue: "0",
        }

        formula = formula.replace(
          `'${id}'`,
          String(
            value?.opfv_cvalue || value?.selected_option?.opfs_coption_value || "<stop>",
          ).replace(",", ".") ?? "0",
        )
      })
      const result = calculate(formula, (resp) => {})

      if (!result) {
        return ""
      }

      return Number(result)
        .toFixed(nPrecision ?? 0)
        .replace(".", ",")
    }

    if (["NUMBER", "CALC", "RANGE"].includes(data?.opfd_ctype)) {
      const currentFormula =
        oValue.opfv_cformula ||
        oCurrentModel.fields.find((field) => field.ombf_opfd_nid === data.opfd_nid)
          ?.ombf_cvalue ||
        ""

      const prefixedValue = `${data?.opfd_cprefix || ""} ${
        data?.opfd_ctype === "CALC"
          ? calculateValue(
              currentFormula /* oValue.opfv_cformula */,
              data?.opfd_nprecision,
            ) || "0"
          : Number(oValue?.opfv_cvalue?.replace(",", ".") || "")
              .toFixed(data?.opfd_nprecision)
              .replace(".", ",") || "0"
      } ${data?.opfd_csuffix || ""}`

      const cPrefixSufix = data?.opfd_cprefix || data?.opfd_csuffix || ""
      const cPrefixSufixWithoutSpaces = cPrefixSufix?.replace(/\s+/g, "")
      const bHasPrefix = data?.opfd_cprefix ? true : false
      const valuerFormated = oValue.opfv_cvalue?.replace(",", ".")
      return {
        opfv_cvalue: oValue.opfv_cvalue, //prefixedValue,//
        value_to_render: prefixedValue !== "  " ? prefixedValue : "0",
        opfv_opfd_nid: data.opfd_nid,
        opfd_cprefix: cPrefixSufixWithoutSpaces || " ",
        bHasPrefix: bHasPrefix,
        opfd_nprecision: data?.opfd_nprecision,
        opfd_cname: data?.opfd_cname,
      }
    }
  }

  function fnCreateTableColumns(oActivity) {
    const columnHelper = createColumnHelper()
    const columns =
      aVisibleFields?.map((data) => {
        const result = oActivity?.values
          ? getValue(oActivity, data)?.opfv_opfd_nid
          : data?.opvf_opfd_nid
        const nFieldId = `fieldid-${result} `
        if (data.opfd_ctype === "DATE") {
          return columnHelper.accessor(nFieldId, {
            accessorKey: `field_${data?.opfd_nid}`,
            id: data?.opfd_nid,
            cell: (info) => info.getValue(),
            header: data.opfd_cname,
            footer: `${data.opfd_ctype}-${data.opfd_cslug}-${oActivity?.status}-.${String(
              data.opvf_opbl_nid,
            )}`,
            sortType: "datetime",
          })
        }
        return columnHelper.accessor(nFieldId, {
          accessorKey: `field_${data?.opfd_nid}`,
          id: data?.opfd_nid,
          cell: (info) => info.getValue(),
          header: data.opfd_cname,
          footer: `${data.opfd_ctype}-${data.opfd_cslug}-${oActivity?.status}-.${String(
            data.opvf_opbl_nid,
          )}`,
          sortingFn: "alphanumericCaseSensitive",
        })
      }) || []

    return columns
  }

  useEffect(() => {
    if (oAllFieldsRes?.loading && !aAllFields?.length) return

    let aDataFOrmated = []
    aData?.map((oActivity) => {
      let fields = []
      fields["blockID"] = oActivity.opat_opbl_nid
      fields["activityID"] = oActivity.opat_nid
      fields["status"] = oActivity?.status
      aVisibleFields.map((data, i) => {
        fields["field_value_id"] = data.opfd_nid
        const result = getValue(oActivity, data)
        fields[`conclusionDate-${data?.opfd_nid}`] = oActivity?.conclusionDate
        fields[`fieldid-${result?.opfv_opfd_nid} `] = result?.opfv_cvalue
        fields[`fieldValueToRender-${data?.opfd_nid}`] = result?.value_to_render
        if (data?.opfd_ctype === "NUMBER" || data?.opfd_ctype === "RANGE") {
          fields[`fieldNumber-${data?.opfd_nid}`] = result?.opfd_cprefix
          fields[`fieldHasPrefix-${data?.opfd_nid}`] = result?.bHasPrefix
          fields[`fieldNumberPrecision-${data?.opfd_nid}`] = result?.opfd_nprecision
          fields[`name-${data?.opfd_nid}`] = data.opfd_cname
          fields[`fieldLimits-${data?.opfd_nid}`] = data.opfd_nmin_ideal
            ? {
                min_limit: data.opfd_nmin_limit,
                min_ideal: data.opfd_nmin_ideal,
                max_ideal: data.opfd_nmax_ideal,
                max_limit: data.opfd_nmax_limit,
              }
            : undefined
        }
        if (data?.opfd_ctype === "SELECTION") {
          fields[`fieldValueToRenderSelect-${data?.opfd_nid}`] =
            result?.value_to_render_select
          fields[`fieldColor-${data?.opfd_nid}`] = result?.oOptionColor
        }
      })
      aDataFOrmated.push(fields)
    })
    fnSetANData(aDataFOrmated)
    if (!aData?.length) {
      const aColumns = fnCreateTableColumns()
      fnSetColumns(aColumns)
      return
    }

    aData.map((oActivity) => {
      const aColumns = fnCreateTableColumns(oActivity)
      fnSetColumns(aColumns)
    })
  }, [aData, aBlocksToList, aAllFields, oAllFieldsRes])

  async function fnOpenModalEditActivity(data) {
    // if (nSelectedEditFieldId !== -1) return
    const response = await fnSubmitGetActivities(Object.values(data)[1])
    const oActivity = response?.data || {}
    fnHandleModalEditActivity({ ...data, ...(oActivity || {}) })
  }

  useEffect(() => {
    if (!aSorting.length) {
      fnSetABlocksASortedOrder([...aBlocks])
      return
    }
    let aTempData = [...aData]
    aTempData = aTempData.slice().sort((a, b) => {
      const aValue =
        String(
          a.values.find((obj) => obj.opfv_opfd_nid === Number(aSorting[0].id))
            ?.opfv_cvalue,
        ) || ""
      const bValue =
        String(
          b.values.find((obj) => obj.opfv_opfd_nid === Number(aSorting[0].id))
            ?.opfv_cvalue,
        ) || ""
      if (aValue != null && bValue != null) {
        return !aSorting[0].desc
          ? aValue?.localeCompare(bValue)
          : bValue.localeCompare(aValue)
      }
      return 0
    })
    aBlocksASortedOrder?.map((block, i) => {
      if (block?.opbl_nid === aTempData[0]?.opat_opbl_nid) {
        const oBlock = { ...block, activities_pending: aTempData }
        fnSetABlocksASortedOrder((prevBlocks) => {
          const updatedBlocks = [...prevBlocks]
          updatedBlocks[i] = oBlock
          return updatedBlocks
        })
      }
    })
  }, [aSorting])

  useEffect(() => {
    const columnOrder = JSON.parse(localStorage.getItem("columnOrder") || "[]")
    const existingBoard = columnOrder.find((column) => column.blockId === oBlock.opbl_nid)
    if (existingBoard) {
      fnSetASorting(existingBoard.order)
    }
    if (!tableScale?.current?.children?.length) return
  }, [])

  // used to reorder the activities in the same block
  async function fnOrderActivitiesState(aPositions, nBlockId) {
    const aBlocks = [...aBlocksASortedOrder] // All block from current board
    const oBlock = aBlocks.find((block) => block.opbl_nid === nBlockId) //get the block that is being changed
    const nBLockIndex = aBlocks.findIndex((block) => block.opbl_nid === nBlockId) // get the index of the block that is being changed
    const aOrderedActivitiesMap = aPositions.reduce((map, obj) => {
      // create a map with the new order of the activities
      if (obj.opat_nid !== undefined && obj.position !== undefined) {
        map[obj.opat_nid] = obj.position
      } else {
        console.error("Invalid object in aPositions:", obj)
      }
      return map
    }, {})

    const aReordedActivities = aData.sort((a, b) => {
      // sort the activities based on the new order
      return aOrderedActivitiesMap[a.opat_nid] - aOrderedActivitiesMap[b.opat_nid]
    })

    oBlock.activities_pending = aReordedActivities?.filter(a => a?.status?.toLowerCase() === "pending")
    oBlock.activities = aReordedActivities?.filter(a => a?.status?.toLowerCase() === "done")
    oBlock.aData = aReordedActivities

    const aBlocksCopy = [...aBlocks || []]
    aBlocksCopy[nBLockIndex] = oBlock
    fnSetABlocksToList(aBlocksCopy)
  }

  async function handleDragEnd(result) {
    if (!result.destination || !aTableRow?.length) return
    if (
      oOnDragUpdate?.source?.droppableId === oOnDragUpdate?.destination?.droppableId &&
      aSorting.length
    )
      return

    if (result.destination.droppableId !== result.source.droppableId) {
      const aSortedCollumns = localStorage.getItem("columnOrder") || "[]"
      const aSortedCollumnsParsed = JSON.parse(aSortedCollumns)
      const aSortedCollumnsFiltered = aSortedCollumnsParsed.some(
        (column) =>
          column.blockId === Number(result.destination.droppableId.split("-")[1]),
      )
      if (aSortedCollumnsFiltered) {
        await fnUpdateActivityBlocks(result)
        return fnMoveActivitySubmit(
          Number(result.draggableId.split("-")[2]),
          Number(result.destination.droppableId.split("-")[1]),
          true,
        )
      }
      await fnUpdateActivityBlocks(result)
      return fnMoveActivitySubmit(
        Number(result.draggableId.split("-")[2]),
        Number(result.destination.droppableId.split("-")[1]),
      )
    }
    let tempData = [...aTableRow]
    let [selectedRow] = tempData.splice(result.source.index, 1)
    tempData.splice(result.destination.index, 0, selectedRow)
    const aNewPosition = tempData.map((temp, i) => {
      return {
        opat_nid: Object.values(temp.original)[1],
        position: i,
      }
    })
    const aNewPositionTableId = tempData.map((temp, i) => ({
      ...temp,
      id: String(i),
    }))
    
    fnSetATableRow(aNewPositionTableId)
    let nBlockId = Number(result.draggableId?.split("-")[1])
    await fnOrderActivitiesState(aNewPosition, nBlockId)
    await fnSubmitNewPositions(aNewPosition, nBlockId)
  }

  async function fnSubmitNewPositions(aNewPosition, nBlockId) {
    await fnReorderActivitiesSubmit(aNewPosition, nBlockId)
  }

  function fnCompareDate(date) {
    if (!date) return false
    try {
      const nTodayTime = new Date(
        fnFormatDate(new Date(), "YYYY-MM-DD HH:mm", false, cUserTimezone),
      ).getTime()
      const nDateTime = new Date(
        fnFormatDate(date, "YYYY-MM-DD HH:mm", true, cUserTimezone),
      ).getTime()
      return nDateTime <= nTodayTime ? true : false
    } catch (e) {
      return false
    }
  }

  const getItemStyle = (item) => {
    let localData = JSON.parse(localStorage.getItem("column_width") || "[]")
    let width = !aSorting.length ? item.getSize() : item.getSize() + 40
    let index = -1
    if (localData.length > 0) {
      localData.map((column, i) => {
        if (column.columnId == item.id && column.block_id === oBlock.opbl_nid) {
          width = column.column_size
          item.column.columnDef.size = column.column_size
          index = i
          return width
        }
      })
    }

    return {
      userSelect: "none",
      width: item.column.getIsResizing() ? item.getSize() : width,
      height: "100%",
      color: "white",
    }
  }

  const scale = aNData?.length > 2 ? "88px" : "44px"
  const [aHead, fnSetAHead] = useState([])
  const [aTableRow, fnSetATableRow] = useState([])
  useEffect(() => {
    let dataTableHead = []
    let dataTableRow = []
    table.getHeaderGroups().map((head) => {
      head.headers?.map((data) => {
        dataTableHead.push(data)
      })
    })

    table.getRowModel().rows.map((row, i) => {
      const cId = aTableRow.length ? aTableRow[i]?.id : String(i)
      const nIndex = aTableRow.length ? aTableRow[i]?.index : i
      dataTableRow.push({
        ...row,
        id: String(cId),
        index: nIndex,
      })
    })
    fnSetAHead(dataTableHead)
    if (aTableRowUnsorted[0] !== -1 && !aSorting) return
    fnSetATableRow(dataTableRow)
  }, [table.getHeaderGroups(), aColumnOrder, aSorting])
  //manager row order state
  useEffect(() => {
    if (!aSorting?.length && aTableRowUnsorted[0] !== -1) {
      fnSetATableRowUnsorted([-1])
      fnSetATableRow(aTableRowUnsorted)
    }
    if (aSorting?.length && aTableRowUnsorted[0] === -1) {
      fnSetATableRowUnsorted(aTableRowUnsorted)
    }
  }, [aSorting])

  useEffect(() => {
    if (aSorting.length) {
      const columnOrder = JSON.parse(localStorage.getItem("columnOrder") || "[]")
      const existingBlockId = columnOrder.find(
        (column) => column.blockId === oBlock.opbl_nid,
      )
      if (existingBlockId) {
        existingBlockId.order = aSorting
      } else {
        columnOrder.push({ blockId: oBlock.opbl_nid, order: aSorting })
      }
      localStorage.setItem("columnOrder", JSON.stringify(columnOrder))
    }
    if (!aSorting.length) {
      const storedColumnOrder = JSON.parse(localStorage.getItem("columnOrder") || "[]")
      const updatedColumnOrder = storedColumnOrder.filter(
        (column) => column.order.length > 0,
      )

      localStorage.setItem("columnOrder", JSON.stringify(updatedColumnOrder))
    }
  }, [aSorting])
  async function handleDragEndColumn(result) {
    if (!result.destination) return
    if (result.destination.droppableId.includes("tBody")) return handleDragEnd(result)

    let tempData = [...aHead]
    let [selectedRow] = tempData.splice(result.source.index, 1)
    tempData.splice(result.destination.index, 0, selectedRow)
    let aNewPosition = await tempData.map((temp, i) => ({
      opfd_nid: temp.id,
      opvf_nposition: i + 1,
    }))

    const reorderColumns = tempData.map((headName) => {
      return headName.id
    })
    table.setColumnOrder(reorderColumns)
    return (aNewPosition = [])
  }

  useEffect(() => {
    if (oDragResult) {
      if (!oDragResult.draggableId.includes("header")) return
      if (oDragResult?.draggableId?.includes("header")) {
        fnSetASorting([])
      }
    }
    return
  }, [oDragResult])

  useEffect(() => {
    if (
      Number(oDragResult?.draggableId?.split("-")[1]) != oBlock.opbl_nid ||
      oDragResult?.draggableId.includes("block")
    )
      return
    handleDragEndColumn(oDragResult)
  }, [oDragResult])

  function FnRowColumns(props) {
    const { data, index, snapshot } = props
    return (
      <>
        <Draggable
          isDragDisabled={bCanChange ? false : true}
          draggableId={`draggable - ${oBlock.opbl_nid} -${data.original.activityID} `}
          key={oBlock.opbl_nid + index}
          index={index}
        >
          {(provided) => (
            <Table.TRow
              ref={provided.innerRef}
              {...provided.draggableProps}
              {...provided.dragHandleProps}
              style={(snapshot.isDragging, provided.draggableProps.style)}
              onClick={() => fnOpenModalEditActivity(data.original)}
            >
              <Table.TData>{<MoveEllipsisIcon />} </Table.TData>
              <Table.TData className={styles.idContainer}>
                <span>{data.original.activityID}</span>
              </Table.TData>

              {data.getVisibleCells().map((cell, i) => {
                const cId = `toolTip - ${oBlock.opbl_nid} -${cell.id} `
                const columnId = cell.id.split("_")[1]
                return (
                  <>
                    <Table.TData key={i} id={`cell${i}`}>
                      {
                        <RcmpTableField
                          onClick={(e) => {
                            e.stopPropagation()
                            e.preventDefault()
                          }}
                          id={cId}
                          className="fieldEditable"
                          data-toolTipId={cId}
                          data={cell.renderValue()}
                          fieldProps={cell}
                          bDisabledEdit={bFieldDisabledEdit}
                          aAllFields={aAllFields}
                          columnId={Number(columnId)}
                          oBlock={oBlock}
                          fnCompareDate={fnCompareDate}
                          cActivityStatus={data.original.status}
                          bCanChange={bCanChange}
                        />
                      }
                    </Table.TData>
                  </>
                )
              })}
            </Table.TRow>
          )}
        </Draggable>
      </>
    )
  }

  return (
    <div className={styles.tableContainer}>
      <Table
        bUpadateArrow={table?.getState()?.columnSizing}
        scale={scale}
        style={{
          background:
            oOnDragUpdate?.destination?.droppableId !==
              oOnDragUpdate?.source?.droppableId &&
            oBlock.opbl_nid ===
              Number(oOnDragUpdate?.destination?.droppableId.split("-")[1]) &&
            aSorting.length &&
            "rgba(108, 182, 227, 0.08)",
          border:
            oOnDragUpdate?.destination?.droppableId !==
              oOnDragUpdate?.source?.droppableId &&
            oBlock.opbl_nid ===
              Number(oOnDragUpdate?.destination?.droppableId.split("-")[1]) &&
            aSorting.length
              ? "4px solid #286B95"
              : "1px solid #CCD1D6",
        }}
      >
        <Table.THead>
          <Table.HeadData className={styles.firstColumn}></Table.HeadData>
          <Table.HeadData className={styles.idColumn}>ID</Table.HeadData>
          {aHead?.map((header, i) => {
            return (
              <FnPortalAwereItem
                key={`block-thead-${i}`}
                item={header}
                flexRender={flexRender}
                index={i}
              />
            )
          })}
        </Table.THead>
        <Droppable droppableId={`tBody - ${oBlock.opbl_nid} `} isDropDisabled={!bCanDrop}>
          {(provided, snapshot) => {
            return (
              <>
                <Table.TBody
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                >
                  {!aTableRow?.length && (
                    <tr style={{ display: "block" }}>
                      {aHead?.map((_, i) => (
                      <td key={`empty-table-placeholder-${i}`} style={{ width: "100%" }} />
                      ))}
                    </tr>
                  )}
                  {aTableRow.map((row, i) => {
                    return (
                      <FnRowColumns key={i} data={row} snapshot={snapshot} index={i} />
                    )
                  })}
                  {provided.placeholder}
                </Table.TBody>
              </>
            )
          }}
        </Droppable>
      </Table>

      <RcmpLoading
        bShow={
          !oActivitiesRequestDone[oBlock.opbl_nid] ||
          (!aAllFields?.length && oAllFieldsRes?.loading)
        }
      />
    </div>
  )
}
